/**
 * @fileoverview AI Decision Test Runner
 *
 * Runs AI decision tests that verify the AI picks the correct destination
 * for a given board position. Unlike AITestRunner (behavioral — any valid move),
 * this checks that the AI moves to a specific expected square.
 *
 * Uses loadTestScenarioFEN + AIOrchestrator, matching the production AI path.
 */

import { ZodiacEngine } from "../zodiacEngine/zodiacEngine";
import { Mode, State } from "../zodiacEngine/types";
import { AIOrchestrator } from "../zodiacEngine/AI/Core/AIOrchestrator";
import { AIPlayerType } from "../zodiacEngine/AI/Core/IAIPlayer";
import {
  AIModel,
  TimeFrame,
  getBaseAITypeFromModel,
  getTimeLimitFromFrame,
  AI_MODEL_CONFIG,
} from "../zodiacEngine/AI/Core/AIVariants";
import { MinimaxAI } from "../zodiacEngine/AI/MinimaxAI";
import { loadTestScenarioFEN } from "../zodiacEngine/Fen/FenLoader";
import { ZodiacFEN } from "../zodiacEngine/Fen/ZodiacFEN";
import { executeAIMove } from "./AIMoveExecutor";
import { AIDecisionTest, AIDecisionResult, AIForcedLineTest, AIForcedLineResult, AIForcedLineStep } from "./AIDecisionTypes";
import { getLegalMoves } from "../zodiacEngine/AI/Core/CommonAIUtils";

/**
 * Run a single AI decision test.
 * Returns whether the AI picked the expected destination.
 */
export async function runAIDecisionTest(
  test: AIDecisionTest,
  aiModel: AIModel,
  timeFrame: TimeFrame = TimeFrame.STANDARD
): Promise<AIDecisionResult> {
  const startTime = performance.now();

  try {
    // 1. Create engine and load FEN
    const engine = new ZodiacEngine();
    loadTestScenarioFEN(engine, test.inputFEN);

    // 2. Force into playing state and set turn
    const gameState = (engine as any).gameStateManager.getGameState();
    gameState.state = State.Playing;
    gameState.mode = Mode.TestAI;
    gameState.turnCount = 10;

    // The FEN already sets isLightTurn via the 'd' prefix.
    // But if the test explicitly says "dark" side and isLightTurn is true,
    // we need to flip it. loadTestScenarioFEN handles 'd' prefix correctly,
    // but the original Jest tests called changeTurns() to make it Dark's turn.
    // So ensure the correct side is active:
    gameState.isLightTurn = test.side === "light";

    // 3. Configure AI via orchestrator
    const aiOrchestrator = new AIOrchestrator(engine);
    const aiType = getBaseAITypeFromModel(aiModel);

    if (test.side === "light") {
      aiOrchestrator.setLightPlayer(aiType);
      aiOrchestrator.setDarkPlayer(AIPlayerType.PLAYER);
    } else {
      aiOrchestrator.setDarkPlayer(aiType);
      aiOrchestrator.setLightPlayer(AIPlayerType.PLAYER);
    }

    // Configure minimax model if applicable
    const modelConfig = AI_MODEL_CONFIG[aiModel];
    if (modelConfig.minimaxModel) {
      const minimaxAI = aiOrchestrator.getAI(AIPlayerType.MINIMAX) as MinimaxAI;
      if (minimaxAI) {
        minimaxAI.setModel(modelConfig.minimaxModel);
      }
    }

    // 4. Request AI move
    const timeLimit = getTimeLimitFromFrame(timeFrame);
    const currentState = engine.getGameState();
    const aiThinkStart = performance.now();
    const moveResult = await aiOrchestrator.requestAIMove(currentState, timeLimit);
    const aiThinkingTimeMs = performance.now() - aiThinkStart;

    if (!moveResult) {
      return {
        test,
        passed: false,
        aiModel: modelConfig.displayName,
        aiThinkingTimeMs: performance.now() - startTime,
        beforeFEN: "",
        afterFEN: "",
        error: "AI returned no move",
      };
    }

    // 5. Check destination
    const actual = moveResult.move.position;
    const expected = test.expectedDestination;
    const destinationMatch = expected
      ? actual.row === expected.row && actual.col === expected.col
      : true; // No expected destination = any destination OK

    // 5b. Check forbidden destinations
    const notForbidden = !test.forbiddenDestinations?.some(
      f => f.row === actual.row && f.col === actual.col
    );

    // 6. Check move type if specified
    const moveTypeMatch =
      !test.expectedMoveType ||
      moveResult.move.moveType === test.expectedMoveType;

    const passed = destinationMatch && moveTypeMatch && notForbidden;

    // 7. Capture before FEN, execute the move, capture after FEN
    const beforeFEN = ZodiacFEN.encode(engine.getGameState());
    await executeAIMove(engine, moveResult, currentState);
    const afterFEN = ZodiacFEN.encode(engine.getGameState());

    return {
      test,
      passed,
      aiModel: modelConfig.displayName,
      actualFromPosition: { row: moveResult.fromPosition.row, col: moveResult.fromPosition.col },
      actualDestination: { row: actual.row, col: actual.col },
      actualMoveType: moveResult.move.moveType,
      aiThinkingTimeMs,
      aiReasoning: moveResult.reasoning,
      principalVariation: moveResult.principalVariation,
      initialBoardValue: moveResult.initialBoardValue,
      rootMoveScores: moveResult.rootMoveScores,
      beforeFEN,
      afterFEN,
      error: passed
        ? undefined
        : !notForbidden
        ? `Moved to forbidden square (${actual.row},${actual.col})`
        : !destinationMatch && expected
        ? `Expected (${expected.row},${expected.col}) but got (${actual.row},${actual.col})`
        : `Expected moveType "${test.expectedMoveType}" but got "${moveResult.move.moveType}"`,
    };
  } catch (error) {
    return {
      test,
      passed: false,
      aiModel: AI_MODEL_CONFIG[aiModel].displayName,
      aiThinkingTimeMs: performance.now() - startTime,
      beforeFEN: "",
      afterFEN: "",
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Run all tests in an AI decision suite
 */
export async function runAIDecisionSuite(
  suite: { name: string; tests: AIDecisionTest[] },
  aiModel: AIModel,
  timeFrame: TimeFrame = TimeFrame.STANDARD
): Promise<{
  suiteName: string;
  results: AIDecisionResult[];
  passed: number;
  failed: number;
  duration: number;
}> {
  const startTime = performance.now();
  const results: AIDecisionResult[] = [];

  for (const test of suite.tests) {
    results.push(await runAIDecisionTest(test, aiModel, timeFrame));
  }

  return {
    suiteName: suite.name,
    results,
    passed: results.filter((r) => r.passed).length,
    failed: results.filter((r) => !r.passed).length,
    duration: performance.now() - startTime,
  };
}

/**
 * Convert position to display notation (e.g., {row:0, col:3} → "D1")
 */
export function posToNotation(pos: { row: number; col: number }): string {
  const cols = "ABCDE";
  return `${cols[pos.col]}${pos.row + 1}`;
}

/**
 * Run a forced line test.
 * Human's moves are applied in sequence, AI responds after each one.
 */
export async function runAIForcedLineTest(
  test: AIForcedLineTest,
  aiModel: AIModel,
  timeFrame: TimeFrame = TimeFrame.STANDARD
): Promise<AIForcedLineResult> {
  const steps: AIForcedLineStep[] = [];

  try {
    // 1. Create engine and load FEN
    const engine = new ZodiacEngine();
    loadTestScenarioFEN(engine, test.inputFEN);

    // 2. Force into playing state
    const gameState = (engine as any).gameStateManager.getGameState();
    gameState.state = State.Playing;
    gameState.mode = Mode.TestAI;
    gameState.turnCount = 10;

    // Human controls one side, AI controls the other
    const humanIsLight = test.side === "light";
    gameState.isLightTurn = humanIsLight; // Human moves first

    // 3. Configure AI for the opposing side
    const aiOrchestrator = new AIOrchestrator(engine);
    const aiType = getBaseAITypeFromModel(aiModel);

    if (humanIsLight) {
      aiOrchestrator.setDarkPlayer(aiType);
      aiOrchestrator.setLightPlayer(AIPlayerType.PLAYER);
    } else {
      aiOrchestrator.setLightPlayer(aiType);
      aiOrchestrator.setDarkPlayer(AIPlayerType.PLAYER);
    }

    // Configure minimax model if applicable
    const modelConfig = AI_MODEL_CONFIG[aiModel];
    if (modelConfig.minimaxModel) {
      const minimaxAI = aiOrchestrator.getAI(AIPlayerType.MINIMAX) as MinimaxAI;
      if (minimaxAI) {
        minimaxAI.setModel(modelConfig.minimaxModel);
      }
    }

    const timeLimit = getTimeLimitFromFrame(timeFrame);

    // 4. Process each forced move
    for (let i = 0; i < test.forcedMoves.length; i++) {
      const forced = test.forcedMoves[i];
      const currentState = (engine as any).gameStateManager.getGameState();

      // Find the matching legal move for the forced move
      const allMoves = getLegalMoves(currentState);
      const matchingMove = allMoves.find(
        (m) =>
          m.fromPosition.row === forced.from.row &&
          m.fromPosition.col === forced.from.col &&
          m.move.position.row === forced.to.row &&
          m.move.position.col === forced.to.col
      );

      if (!matchingMove) {
        return {
          test,
          steps,
          finalFEN: ZodiacFEN.encode(engine.getGameState()),
          error: `Step ${i + 1}: No legal move from (${forced.from.row},${forced.from.col}) to (${forced.to.row},${forced.to.col}). Available moves: ${allMoves.map(m => `${posToNotation(m.fromPosition)}->${posToNotation(m.move.position)}`).join(', ')}`,
        };
      }

      // Look up piece from current internal state
      const fromSquare = currentState.board[forced.from.row][forced.from.col];
      const piece = fromSquare.Pieces.find(
        (p: any) => p.id === matchingMove.move.pieceId
      );

      if (!piece) {
        return {
          test,
          steps,
          finalFEN: ZodiacFEN.encode(engine.getGameState()),
          error: `Step ${i + 1}: Could not find piece at (${forced.from.row},${forced.from.col})`,
        };
      }

      // Build human move description
      const color = humanIsLight ? "Light" : "Dark";
      const humanMoveDesc = `${color} ${piece.type} ${posToNotation(forced.from)}->${posToNotation(forced.to)}`;

      // Execute the human's forced move
      let toPiece = matchingMove.move.toPiece;
      if (toPiece) {
        const toSquare = currentState.board[forced.to.row][forced.to.col];
        const currentToPiece = toSquare.Pieces.find(
          (p: any) => p.type === toPiece!.type && p.isLight === toPiece!.isLight
        );
        toPiece = currentToPiece || toPiece;
      }

      await engine.makeMove(
        forced.from,
        forced.to,
        piece,
        toPiece,
        matchingMove.move.moveType,
        matchingMove.move.sideEffects
      );

      const humanMoveFEN = ZodiacFEN.encode(engine.getGameState());

      // Check if game ended after human move
      const stateAfterHuman = engine.getGameState();
      if (stateAfterHuman.state === State.PostGame || stateAfterHuman.state === State.Draw) {
        const result = stateAfterHuman.lightWon === true ? "Light wins" :
                       stateAfterHuman.lightWon === false ? "Dark wins" : "Draw";
        steps.push({
          stepNumber: i + 1,
          humanMove: humanMoveDesc,
          humanMoveFEN,
          aiMove: "(game over)",
          aiMoveFEN: humanMoveFEN,
          aiThinkingTimeMs: 0,
          gameOver: true,
          gameResult: result,
        });
        return { test, steps, finalFEN: humanMoveFEN };
      }

      // Now run AI for the response
      const aiState = engine.getGameState();
      const aiThinkStart = performance.now();
      const aiMoveResult = await aiOrchestrator.requestAIMove(aiState, timeLimit);
      const aiThinkingTimeMs = performance.now() - aiThinkStart;

      if (!aiMoveResult) {
        steps.push({
          stepNumber: i + 1,
          humanMove: humanMoveDesc,
          humanMoveFEN,
          aiMove: "(no move returned)",
          aiMoveFEN: humanMoveFEN,
          aiThinkingTimeMs,
        });
        return { test, steps, finalFEN: humanMoveFEN, error: `Step ${i + 1}: AI returned no move` };
      }

      // Build AI move description
      const aiPiece = aiState.board[aiMoveResult.fromPosition.row]?.[aiMoveResult.fromPosition.col]
        ?.Pieces?.find((p: any) => p.id === aiMoveResult.move.pieceId);
      const aiColor = humanIsLight ? "Dark" : "Light";
      const aiMoveDesc = `${aiColor} ${aiPiece?.type || '?'} ${posToNotation(aiMoveResult.fromPosition)}->${posToNotation(aiMoveResult.move.position)}`;

      // Execute AI move
      await executeAIMove(engine, aiMoveResult, aiState);
      const aiMoveFEN = ZodiacFEN.encode(engine.getGameState());

      // Check if game ended after AI move
      const stateAfterAI = engine.getGameState();
      const gameOver = stateAfterAI.state === State.PostGame || stateAfterAI.state === State.Draw;
      const gameResult = gameOver
        ? (stateAfterAI.lightWon === true ? "Light wins" :
           stateAfterAI.lightWon === false ? "Dark wins" : "Draw")
        : undefined;

      steps.push({
        stepNumber: i + 1,
        humanMove: humanMoveDesc,
        humanMoveFEN,
        aiMove: aiMoveDesc,
        aiMoveFEN,
        aiReasoning: aiMoveResult.reasoning,
        aiThinkingTimeMs,
        rootMoveScores: aiMoveResult.rootMoveScores,
        principalVariation: aiMoveResult.principalVariation,
        initialBoardValue: aiMoveResult.initialBoardValue,
        gameOver,
        gameResult,
      });

      if (gameOver) {
        return { test, steps, finalFEN: aiMoveFEN };
      }
    }

    return { test, steps, finalFEN: ZodiacFEN.encode(engine.getGameState()) };
  } catch (error) {
    return {
      test,
      steps,
      finalFEN: "",
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
